import { useState, useEffect, useRef } from 'react';
import API from '../../api/api';
import { toast } from 'react-hot-toast';

const JobInfoForm = ({ projectId, formData, setFormData, setActiveTab, projectType, country, isNewProject }) => {
  const [customers, setCustomers] = useState([]);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingJobNumber, setLoadingJobNumber] = useState(false);
  const [services, setServices] = useState([]);
  
  const hasFetchedJobNumber = useRef(false);

  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [custRes, clientRes,serviceRes] = await Promise.all([
          API.get('customer/'),
          API.get('client/'),
          API.get("service/"),
         
        ]);
        setCustomers(custRes.data);
        setClients(clientRes.data);
        setServices(serviceRes.data);
        
      } catch (err) {
        console.error('Error loading master data:', err);
      }
    };
    fetchMasters();
  }, []);

  useEffect(() => {
    // Only fetch job number for new projects without an existing job number
    if (isNewProject && !formData.job_number && !hasFetchedJobNumber.current && projectType) {
      const fetchNextJobNumber = async () => {
        setLoadingJobNumber(true);
        try {
          const params = { project_type: projectType, country: country || "" };
          const res = await API.get("jobs/next-number/", { params });
          setFormData(prev => ({
            ...prev,
            job_number: res.data.next_job_number,
          }));
          hasFetchedJobNumber.current = true;
        } catch (err) {
          console.error("Failed to fetch next job number:", err);
          toast.error("Failed to generate job number");
        } finally {
          setLoadingJobNumber(false);
        }
      };

      fetchNextJobNumber();
    }
  }, [isNewProject, formData.job_number, projectType, country, setFormData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isFormComplete = [
    'job_number',
    'customer_name',
    'client_name',
    'service_name',
    'rig_number',
    'well_name',
    'well_id',
    'survey_method',
    'start_date',
  ].every((field) => formData[field]?.toString().trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectId) return;

    setSaving(true);
    try {
      if (formData.id) {
        await API.put(`jobs/${formData.id}/`, { 
          ...formData, 
          project: projectId 
        });
        toast.success('Job info updated successfully');
      } else {
        const res = await API.post('jobs/', { 
          ...formData, 
          project: projectId 
        });
        setFormData(res.data);
        toast.success('Job info saved successfully');
      }
      setActiveTab('Well Info');
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save job info');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
      <div>
        <label className="block mb-1 font-medium">Job Number</label>
        {loadingJobNumber ? (
          <div className="w-full px-2 py-1.5 border rounded-lg text-xs bg-gray-100 animate-pulse">
            Generating job number...
          </div>
        ) : (
          <input
            name="job_number"
            type="text"
            value={formData.job_number || ''}
            onChange={handleChange}
            readOnly={!isNewProject}
            className={`w-full px-2 py-1.5 border rounded-lg text-xs ${
              !isNewProject ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
            }`}
          />
        )}
      </div>

      <div>
        <label className="block mb-1 font-medium">Customer</label>
        <select
          name="customer_name"
          value={formData.customer_name}
          onChange={handleChange}
          className="w-full px-2 py-1.5 border rounded-lg text-xs bg-white"
        >
          <option value="">Select Customer</option>
          {customers.map((c) => (
            <option key={c.customer_id} value={c.customer_id}>{c.customer_name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block mb-1 font-medium">Client</label>
        <select
          name="client_name"
          value={formData.client_name}
          onChange={handleChange}
          className="w-full px-2 py-1.5 border rounded-lg text-xs bg-white"
        >
          <option value="">Select Client</option>
          {clients.map((c) => (
            <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block mb-1 font-medium">Rig Number</label>
        <input
          name="rig_number"
          type="text"
          value={formData.rig_number}
          onChange={handleChange}
          className="w-full px-2 py-1.5 border rounded-lg text-xs bg-white"
        />
      </div>

      <div>
        <label className="block mb-1 font-medium">Well Name</label>
        <input
          name="well_name"
          type="text"
          value={formData.well_name}
          onChange={handleChange}
          className="w-full px-2 py-1.5 border rounded-lg text-xs bg-white"
        />
      </div>
      <div>
  <label className="block mb-1 font-medium">Service</label>
  <select
    name="service_name"
    value={formData.service_name}
    onChange={handleChange}
    className="w-full px-2 py-1.5 border rounded-lg text-xs bg-white"
  >
    <option value="">Select Service</option>
    {services.map((s) => (
      <option key={s.service_id} value={s.service_id}>
        {s.service_name}
      </option>
    ))}
  </select>
</div>

      <div>
        <label className="block mb-1 font-medium">Well ID</label>
        <input
          name="well_id"
          type="text"
          value={formData.well_id}
          onChange={handleChange}
          className="w-full px-2 py-1.5 border rounded-lg text-xs bg-white"
        />
      </div>
      <div>
        <label className="block mb-1 font-medium">Survey Method</label>
        <input
          name="survey_method"
          type="text"
          value={formData.survey_method}
          readOnly
          className="w-full px-2 py-1.5 border rounded-lg text-xs bg-gray-200 cursor-not-allowed"
        />
      </div>

      <div>
        <label className="block mb-1 font-medium">Start Date</label>
            <input
            name="start_date"
            type="date"
            value={formData.start_date}
            onChange={handleChange}
            min={new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) 
              .toISOString()
              .split("T")[0]}
            max={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) 
              .toISOString()
              .split("T")[0]}
            className="w-full px-2 py-1.5 border rounded-lg text-xs bg-white"
          />
      </div>

      <div className="md:col-span-2 text-right mt-2">
        <button
          type="submit"
          disabled={!isFormComplete || saving}
          className={`px-4 py-2 rounded-lg text-xs transition-all ${
            isFormComplete && !saving
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {saving 
            ? (formData.id ? 'Updating...' : 'Saving...') 
            : (formData.id ? 'UPDATE JOB INFO' : 'Save & Continue')
          }
        </button>
      </div>
    </form>
  );
};

export default JobInfoForm;